import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList } from "lucide-react";
import { pad, wdLabel } from "@/lib/yasamaru";

export function DayByDayDialog({ user, period, days, closedSet, requests, shifts, onClose }) {
  if (!user || !period) return null;
  const reqByDate = {};
  for (const r of requests) {
    if (r.user_id === user.user_id) reqByDate[r.date] = r;
  }
  const shiftByDate = {};
  for (const s of shifts) {
    if (s.user_id === user.user_id) shiftByDate[s.date] = s;
  }
  const sym = (t) => t === "available" ? "◎ 入れます" : t === "either" ? "△ 必要なら" : t === "unavailable" ? "× 入れません" : "未提出";
  const reqTone = (t) => t === "available" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : t === "either" ? "text-amber-700 bg-amber-50 border-amber-200"
                      : t === "unavailable" ? "text-rose-700 bg-rose-50 border-rose-200"
                      : "text-slate-500 bg-slate-50 border-slate-200";
  const dayList = [];
  for (let d = 1; d <= days; d++) {
    const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
    const wd = new Date(period.year, period.month - 1, d).getDay();
    dayList.push({ d, wd, dateStr, isClosed: closedSet.has(dateStr), r: reqByDate[dateStr], s: shiftByDate[dateStr] });
  }
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="day-by-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />{user.display_name || user.name} さんの希望 / 配置
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-600 mb-2 flex flex-wrap gap-3">
          <span>◎ <b className="text-emerald-700">{user.requested_days}</b>日</span>
          <span>△ <b className="text-amber-700">{user.either_days}</b>日</span>
          <span>× <b className="text-rose-700">{user.unavailable_days}</b>日</span>
          <span className="ml-auto text-blue-700">配置済み <b>{user.total_shift_days}</b>日</span>
        </div>
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
          {dayList.map(({ d, wd, isClosed, r, s }) => (
            <li key={d} className={`px-3 py-1.5 grid grid-cols-[60px_1fr_1fr] items-center gap-2 text-sm ${isClosed ? "bg-slate-50" : ""}`}>
              <div className="flex items-center gap-1">
                <span className={`font-bold ${wd === 0 ? "text-red-500" : wd === 6 ? "text-blue-500" : "text-slate-700"}`}>{d}日</span>
                <span className="text-[10px] text-slate-400">({wdLabel(wd)})</span>
              </div>
              <div>
                {isClosed ? <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">休業</span> : (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${reqTone(r?.request_type)}`}>{sym(r?.request_type)}</span>
                    {r?.start_time && r?.end_time && <span className="text-[10px] text-slate-500">{r.start_time}〜{r.end_time}</span>}
                  </div>
                )}
              </div>
              <div className="text-right">
                {isClosed ? <span className="text-[10px] text-slate-300">—</span> : s ? (
                  <div className="inline-flex flex-col items-end gap-0.5">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border font-semibold ${
                      s.status === "confirmed" ? "text-green-700 bg-green-50 border-green-200" :
                      s.status === "changed" ? "text-yellow-700 bg-yellow-50 border-yellow-200" :
                      "text-blue-700 bg-blue-50 border-blue-200"
                    }`}>
                      ✓ {s.start_time && s.end_time ? `${s.start_time}〜${s.end_time}` : "配置済"}
                    </span>
                    {s.assignment && (
                      <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded px-1.5">
                        {s.assignment}
                      </span>
                    )}
                  </div>
                ) : <span className="text-[10px] text-slate-400">—</span>}
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="day-by-day-close">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
